import { Slot } from '@radix-ui/react-slot';
import type { IconComponent, Option } from '@tbitop/ui';
import { Avatar, Paragraph, Select } from '@tbitop/ui';
import classNames from 'classnames';
import type { ComponentProps, PropsWithChildren } from 'react';


import { useUser } from '@tbitop/authentication';
import { PartnerSelect } from './partner-select';

type Mandant = {
  anzeigename: string;
  hasSalesPlanner: boolean;
  keycloakOrganisationId: string;
};

type SideMenuUserItemProps = {
  user: ComponentProps<typeof Avatar>['user'];
  currentMandant: Mandant | null;
  mandanten?: Mandant[];
  onMandantSelect: (mandantId: string) => void;
};

export const SideMenuUserItem = ({ user, currentMandant, mandanten = [], onMandantSelect }: SideMenuUserItemProps) => {
  const mandantenOptions: Option[] = mandanten.map(mandant => ({ label: mandant.anzeigename, value: mandant.keycloakOrganisationId }));
  const displayFilter = mandantenOptions.length > 10;

  return (
    <div className="grid grid-rows-[0fr_0fr] grid-cols-[0fr_minmax(0,_1fr)] gap-150" aria-label="Nutzersitzung">
      <span className="w-600 h-600 row-span-2">
        <Avatar user={user}/>
      </span>
      <Paragraph className="tds-user-item-username">{user?.displayName}</Paragraph>
      <div className="flex flex-col gap-5">
        {mandantenOptions.length > 1 ? (
          <label>
            <span className="tds-input-label">Organisation:</span>
            <Select
              addEmptyOption={false}
              displayOptionsFilter={displayFilter}
              className="w-full"
              variant="small"
              onValueChange={id => {
                if (id === currentMandant?.keycloakOrganisationId) {
                  return;
                }
                onMandantSelect(id);
              }}
              options={mandantenOptions}
              value={currentMandant?.keycloakOrganisationId}
            />
          </label>
        ) : null}
        <IfPartnerbetreuer>
          <PartnerSelect />
        </IfPartnerbetreuer>
      </div>
    </div>
  );
};

export const SideMenuItems = ({ children }: PropsWithChildren<unknown>) => (
  <ul className="flex flex-col items-start flex-1 gap-300">
    {children}
  </ul>
);

type SideMenuItemProps = SideMenuItemRootProps & SideMenuItemContentProps;

export const SideMenuItem = ({ className, Icon, name }: SideMenuItemProps) => (
  <SideMenuItem.Root className={className}>
    <SideMenuItem.Content Icon={Icon} name={name}></SideMenuItem.Content>
  </SideMenuItem.Root>
);

type SideMenuItemRootProps = {
  className?: string;
};

SideMenuItem.Root = ({ className, children }: PropsWithChildren<SideMenuItemRootProps>) => (
  <li>
    <Slot className={classNames('tds-side-menu-item inline-block', className)}>
      {children}
    </Slot>
  </li>
);

type SideMenuItemContentProps = {
  Icon: IconComponent;
  name: string;
  className?: string;
};

SideMenuItem.Content = ({ Icon, name, className }: SideMenuItemContentProps) => (
  <span className={classNames('flex gap-100', className)}>
    <Icon />
    {name}
  </span>
);

const IfPartnerbetreuer = ({ children }: PropsWithChildren<unknown>) => {
  const user = useUser();
  const isPartnerbetreuer = user?.activeOrganization?.role.find(role => role.toLocaleLowerCase() === 'partnerbetreuer');

  return isPartnerbetreuer ? children : null;
};
